import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../contexts/SiteContext';
import { messageAPI } from '../../api';

interface NavProps {
  isOpen?: boolean;
}

interface OverlayProps {
  isOpen?: boolean;
}

const HeaderContainer = styled.header`
  width: 100%;
  background-color: white;
`;

const TopBar = styled.div`
  background-color: white;
  border-bottom: 1px solid #eaeaea;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  span {
    font-size: 0.875rem;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  width: 12rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 10rem;
  }
`;

const HeaderLinks = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderLink = styled(Link)`
  color: #374151;
  margin-left: 1rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    color: #ef4444;
  }
`;

const NavBar = styled.div`
  background-color: white;
  border-bottom: 1px solid #eaeaea;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const NavBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const NavBarInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavBarLeft = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: space-between;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ef4444;
  display: flex;
  align-items: center;
  margin-right: 2rem;
  
  span {
    color: #111827;
    font-weight: 800;
  }
  
  strong {
    color: #ef4444;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: 1rem;
  }
`;

const MobileOverlay = styled.div<OverlayProps>`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${props => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;

const Navigation = styled.nav<NavProps>`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-280px')};
    left: auto;
    width: 280px;
    height: 100vh;
    background-color: white;
    flex-direction: column;
    gap: 0;
    padding: 1rem 0;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    z-index: 1001;
    overflow-y: auto;
  }
`;

const NavLink = styled(Link)`
  color: #111827;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    color: #ef4444;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #f3f4f6;
    width: 100%;
  }
`;

const MobileHeader = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #f3f4f6;
    margin-bottom: 1rem;
  }
`;

const CloseButton = styled.button`
  font-size: 1.5rem;
  color: #4b5563;
  background: none;
  border: none;
`;

const LoginButton = styled.button`
  background-color: white;
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.25rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    background-color: #fee2e2;
  }
`;

const RegisterButton = styled(Link)`
  background-color: #ef4444;
  color: white;
  border-radius: 0.25rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const CurrentTime = styled.div`
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
  font-weight: 500;
`;

const LogoImage = styled.img`
  height: 2.5rem;
  object-fit: contain;
  margin-right: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 2rem;
    margin-right: 0.5rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  color: #4b5563;
  font-size: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileActions = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 1.5rem;
    margin-top: 1rem;
    border-top: 1px solid #f3f4f6;
  }
`;

const MobileAuthButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  & > button, & > a {
    flex: 1;
    display: flex;
    justify-content: center;
  }
`;

const MobileSearchContainer = styled.div`
  position: relative;
  margin-top: 0.5rem;
  
  input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
  }
  
  span {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }
`;

const MobileLinkButton = styled(Link)`
  display: block;
  text-align: center;
  background-color: #f3f4f6;
  color: #4b5563;
  border-radius: 0.375rem;
  padding: 0.5rem;
  font-size: 0.875rem;
  margin: 0.5rem 0;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const MobileUserInfo = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #f9fafb;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin: 0.75rem 0;
`;

const UserInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  
  &:not(:last-child) {
    border-right: 1px solid #e5e7eb;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  display: flex;
  align-items: center;
`;

const Badge = styled.span`
  background-color: #ef4444;
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  border-radius: 9999px;
  padding: 0.125rem 0.375rem;
  margin-left: 0.25rem;
`;

const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { siteConfig } = useSite();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [mobileSearchText, setMobileSearchText] = useState('');
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    // 모바일 메뉴가 열려있을 때 스크롤 막기
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // 모바일 메뉴가 열려있을 때 외부 클릭 시 닫기
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return date.toLocaleDateString('ko-KR', options);
  };

  const addToFavorites = () => {
    if (document.all) {
      (window.external as any).AddFavorite(window.location.href, document.title);
    }
    // Google Chrome   
    else if ((window as any).chrome) {
      alert("Ctrl+D키를 누르시면 즐겨찾기에 추가하실 수 있습니다.");
    }
    // Firefox   
    else if ((window as any).sidebar) {
      (window as any).sidebar.addPanel(document.title, window.location.href, "");
    }
    // Opera   
    else if ((window as any).opera && (window as any).print) {
      var elem = document.createElement('a');
      elem.setAttribute('href', window.location.href);
      elem.setAttribute('title', document.title);
      elem.setAttribute('rel', 'sidebar');
      elem.click();
    }
  };

  // 안 읽은 쪽지 수 조회
  const fetchUnreadMessagesCount = async () => {
    if (isAuthenticated && user) {
      try {
        const response = await messageAPI.getUnreadCount();
        if (response.data && response.data.success) {
          setUnreadMessages(response.data.unreadCount);
        }
      } catch (error) {
        console.error('안 읽은 쪽지 수 조회 오류:', error);
      }
    }
  };
  
  // 인증 상태 변경 시 안 읽은 쪽지 수 조회
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUnreadMessagesCount();
    } else {
      setUnreadMessages(0);
    }
  }, [isAuthenticated, user]);

  // 검색 제출 핸들러
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchText)}&type=1`);
      setSearchText('');
    }
  };

  // 모바일 검색 제출 핸들러
  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileSearchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchText)}&type=1`);
      setMobileSearchText('');
      setMobileMenuOpen(false);
    }
  };

  // 검색 아이콘 클릭 핸들러
  const handleSearchIconClick = () => {
    if (searchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchText)}&type=1`);
      setSearchText('');
    }
  };

  // 모바일 검색 아이콘 클릭 핸들러
  const handleMobileSearchIconClick = () => {
    if (mobileSearchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchText)}&type=1`);
      setMobileSearchText('');
      setMobileMenuOpen(false);
    }
  };

  return (
    <HeaderContainer>
      <TopBar>
        <TopBarContent>
          <TopBarLeft>
            <span style={{ color: "#eab308", cursor: "pointer" }} onClick={addToFavorites}>⭐</span>
            <span style={{ cursor: "pointer" }} onClick={addToFavorites}>즐겨찾기 추가</span>
            {/* <span style={{ color: "#9ca3af" }}>•</span>
            <span>새글</span> */}
            <span style={{ color: "#9ca3af" }}>•</span>
            <CurrentTime>{formatDate(currentDate)}</CurrentTime>
          </TopBarLeft>
          <TopBarRight>
            <div style={{ display: "flex", alignItems: "center" }} onClick={() => navigate('/q/event')}>
              <span style={{ color: "#3b82f6" }}>🎁</span>
              <span style={{ fontSize: "0.875rem", marginLeft: "0.25rem" }}>이벤트</span>
            </div>
            <SearchContainer>
              <form onSubmit={handleSearch} style={{ display: 'flex', width: '100%', position: 'relative' }}>
                <SearchInput 
                  type="text" 
                  placeholder="원하는 검색어를 입력하세요." 
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <span 
                  style={{
                    position: "absolute",
                    right: "0.5rem",
                    top: "0.375rem",
                    color: "#9ca3af",
                    cursor: "pointer"
                  }}
                  onClick={handleSearchIconClick}
                >🔍</span>
              </form>
            </SearchContainer>
            {isAuthenticated && user ? (
              <>
                <UserInfo>
                  <span>{user.username}님</span>
                  <LoginButton onClick={handleLogout}>로그아웃</LoginButton>
                </UserInfo>
              </>
            ) : (
              <>
                <LoginButton onClick={() => navigate('/login')}>로그인</LoginButton>
                <RegisterButton to="/register">회원가입</RegisterButton>
              </>
            )}
          </TopBarRight>
        </TopBarContent>
      </TopBar>

      <NavBar>
        <NavBarContent>
          <NavBarInner>
            <NavBarLeft>
              <Logo to="/">
                <LogoImage
                  src={siteConfig.logoPath}
                  alt={siteConfig.siteName}
                />
              </Logo>
              <MobileOverlay isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
              <Navigation isOpen={mobileMenuOpen} ref={navRef}>
                <MobileHeader>
                  <span>메뉴</span>
                  <CloseButton onClick={() => setMobileMenuOpen(false)}>✕</CloseButton>
                </MobileHeader>
                <NavLink to="/" onClick={() => setMobileMenuOpen(false)}>
                  홈
                </NavLink>
                <NavLink to="/boards/1" onClick={() => setMobileMenuOpen(false)}>
                  공지사항
                </NavLink>
                <NavLink to="/boards/2" onClick={() => setMobileMenuOpen(false)}>
                  자유게시판
                </NavLink>
                <NavLink to="/boards/3" onClick={() => setMobileMenuOpen(false)}>
                  먹튀신고
                </NavLink>
                <NavLink to="/certified" onClick={() => setMobileMenuOpen(false)}>
                  공식보증업체 <span style={{ color: "#eab308", marginLeft: "0.25rem" }}>⭐</span>
                </NavLink>
                <NavLink to="/QEVENT" onClick={() => setMobileMenuOpen(false)}>
                  메인 이벤트
                </NavLink>
                {/*
                <NavLink to="/boards/5" onClick={() => setMobileMenuOpen(false)}>
                  일반홍보
                </NavLink>
                <NavLink to="/boards/6" onClick={() => setMobileMenuOpen(false)}>
                  구인구직
                </NavLink>
                */}
                <MobileActions>
                  <MobileSearchContainer>
                    <form onSubmit={handleMobileSearch}>
                      <input 
                        type="text" 
                        placeholder="검색어를 입력하세요" 
                        value={mobileSearchText}
                        onChange={(e) => setMobileSearchText(e.target.value)}
                      />
                      <span onClick={handleMobileSearchIconClick}>🔍</span>
                    </form>
                  </MobileSearchContainer>

                  {isAuthenticated && user ? (
                    <>
                      <div>
                        <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                          <strong>{user.username}</strong>님 환영합니다
                        </p>
                        
                        <MobileUserInfo>
                          <UserInfoItem onClick={() => {
                            navigate('/mypage/points');
                            setMobileMenuOpen(false);
                          }}>
                            <InfoLabel>포인트</InfoLabel>
                            <InfoValue>{user.points?.toLocaleString() || 0}P</InfoValue>
                          </UserInfoItem>
                          <UserInfoItem onClick={() => {
                            navigate('/mypage/messages');
                            setMobileMenuOpen(false);
                          }}>
                            <InfoLabel>쪽지</InfoLabel>
                            <InfoValue>
                              {unreadMessages > 0 ? (
                                <>
                                  <Badge>{unreadMessages}</Badge>
                                </>
                              ) : (
                                '0'
                              )}
                            </InfoValue>
                          </UserInfoItem>
                        </MobileUserInfo>
                        
                        <MobileLinkButton to="/mypage/info">
                          내 정보 관리
                        </MobileLinkButton>
                        
                        <LoginButton onClick={handleLogout} style={{ width: '100%' }}>
                          로그아웃
                        </LoginButton>
                      </div>
                    </>
                  ) : (
                    <MobileAuthButtons>
                      <LoginButton onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}>로그인</LoginButton>
                      <RegisterButton to="/register" onClick={() => setMobileMenuOpen(false)}>
                        회원가입
                      </RegisterButton>
                    </MobileAuthButtons>
                  )}
                </MobileActions>
              </Navigation>
            </NavBarLeft>
            <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
              <span>☰</span>
            </MobileMenuButton>
          </NavBarInner>
        </NavBarContent>
      </NavBar>
    </HeaderContainer>
  );
};

export default Header; 